import * as Sentry from '@sentry/react';
import type { StackFrame } from '@sentry/core';

import { PROD, ZVUK_SENTRY_ENV } from 'application/consts';

const scrubSensitiveUrl = (url: string | undefined): string | undefined => {
  if (!url) return url;

  const SENSITIVE_PARAMS = ['token', 'uid', 'code', 'state', 'jwt', 'JWT'];

  try {
    const parsed = new URL(url);

    SENSITIVE_PARAMS.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[Filtered]');
      }
    });

    return parsed.toString();
  } catch {
    return url.replace(/([?&])(token|uid|code|state|jwt)=([^&]*)/gi, '$1$2=[Filtered]');
  }
};

const getFrames = (event: Sentry.ErrorEvent): StackFrame[] =>
  event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];

const isBrowserExtensionEvent = (event: Sentry.ErrorEvent): boolean => {
  const frames = getFrames(event);

  const hasExtensionFrame = frames.some((f) => {
    const filename = f.filename ?? '';

    return (
      filename.startsWith('chrome-extension://') ||
      filename.startsWith('moz-extension://') ||
      filename.startsWith('safari-web-extension://') ||
      filename.includes('injectedScript') ||
      filename === '/login'
    );
  });

  if (hasExtensionFrame) return true;

  return frames.some((f) => (f.filename ?? '') === '<anonymous>' && (f.function ?? '').includes('HTMLDocument'));
};

const isNetworkFetchError = (event: Sentry.ErrorEvent): boolean =>
  event.exception?.values?.some((v) => {
    const msg = v.value ?? '';

    return (
      msg === 'Failed to fetch' ||
      msg === 'Load failed' ||
      msg.startsWith('NetworkError when attempting to fetch resource')
    );
  }) ?? false;

const isThirdPartyScriptError = (event: Sentry.ErrorEvent): boolean =>
  getFrames(event).some((f) => {
    const filename = f.filename ?? '';
    return filename.includes('/dist/usetiful.js') || filename.includes('/dist/u-content-loader.js');
  });

const isExpectedLogoutError = (event: Sentry.ErrorEvent): boolean =>
  (event.request?.url ?? '').includes('/logout') &&
  (event.exception?.values?.some((v) => (v.value ?? '').includes('Refresh token is required')) ?? false);

const scrubEventUrls = (event: Sentry.ErrorEvent): Sentry.ErrorEvent => {
  const scrubbedRequest = event.request?.url
    ? { ...event.request, url: scrubSensitiveUrl(event.request.url) ?? event.request.url }
    : event.request;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const breadcrumbs = event.breadcrumbs as any as
    | Array<{ data?: { url?: string }; [key: string]: unknown }>
    | undefined;

  const scrubbedBreadcrumbs = breadcrumbs?.map((bc) => {
    if (!bc.data?.url) return bc;
    return { ...bc, data: { ...bc.data, url: scrubSensitiveUrl(bc.data.url) ?? bc.data.url } };
  });

  const scrubbedTags = event.tags?.transaction
    ? { ...event.tags, transaction: scrubSensitiveUrl(String(event.tags.transaction)) ?? event.tags.transaction }
    : event.tags;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return { ...event, request: scrubbedRequest, breadcrumbs: scrubbedBreadcrumbs, tags: scrubbedTags } as any;
};

Sentry.init({
  dsn: 'https://029a44aeea3b44298743622d0c818eb3@sentry.muzlab.ru/21',
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(PROD ? [] : [Sentry.breadcrumbsIntegration({ console: false })]),
  ],
  environment: ZVUK_SENTRY_ENV || 'local',
  release: import.meta.env.ZVUK_SENTRY_RELEASE,
  tracesSampleRate: PROD ? 0.1 : 0,
  beforeSend: (event) => {
    if (isBrowserExtensionEvent(event)) return null;
    if (isNetworkFetchError(event)) return null;
    if (isThirdPartyScriptError(event)) return null;
    if (isExpectedLogoutError(event)) return null;

    return scrubEventUrls(event);
  },
});
